import React from 'react';
import { useTradingPermissions } from '../hooks/useTradingPermissions';
import { ModulePermission, PermissionAction } from '../types/permissions';

interface TradingPermissionGateProps {
    module: ModulePermission;
    action: PermissionAction;
    children: React.ReactNode;
    fallback?: React.ReactNode;
    requireAll?: boolean; // If true, requires all permissions in the array
}

interface TradingPermissionGroupProps {
    permissions: Array<{ module: ModulePermission; action: PermissionAction }>;
    children: React.ReactNode;
    fallback?: React.ReactNode;
    requireAll?: boolean; // If true, requires all permissions, otherwise any permission
}

/**
 * Trading-specific permission gate component
 * Provides easy permission checking for trading-related features
 */
export const TradingPermissionGate: React.FC<TradingPermissionGateProps> = ({
    module,
    action,
    children,
    fallback = null,
}) => {
    const {
        canViewOrders,
        canCreateOrders,
        canEditOrders,
        canDeleteOrders,
        canExportOrders,
        canViewTrades,
        canCreateTrades,
        canEditTrades,
        canDeleteTrades,
        canExportTrades,
        canViewPositions,
        canCreatePositions,
        canEditPositions,
        canViewCustomers,
        canEditCustomers,
        canViewTradingAccounts,
        canEditTradingAccounts,
        canViewTransactions,
        canEditTransactions,
    } = useTradingPermissions();

    // Map module to specific permission check
    const checkPermission = () => {
        switch (module) {
            case ModulePermission.ORDERS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return canViewOrders();
                    case PermissionAction.CREATE:
                        return canCreateOrders();
                    case PermissionAction.EDIT:
                        return canEditOrders();
                    case PermissionAction.DELETE:
                        return canDeleteOrders();
                    case PermissionAction.EXPORT:
                        return canExportOrders();
                    default:
                        return false;
                }
            case ModulePermission.TRADES:
                switch (action) {
                    case PermissionAction.VIEW:
                        return canViewTrades();
                    case PermissionAction.CREATE:
                        return canCreateTrades();
                    case PermissionAction.EDIT:
                        return canEditTrades();
                    case PermissionAction.DELETE:
                        return canDeleteTrades();
                    case PermissionAction.EXPORT:
                        return canExportTrades();
                    default:
                        return false;
                }
            case ModulePermission.TRADING:
                switch (action) {
                    case PermissionAction.VIEW:
                        return canViewPositions();
                    case PermissionAction.CREATE:
                        return canCreatePositions();
                    case PermissionAction.EDIT:
                        return canEditPositions();
                    default:
                        return false;
                }
            case ModulePermission.CUSTOMERS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return canViewCustomers();
                    case PermissionAction.EDIT:
                        return canEditCustomers();
                    default:
                        return false;
                }
            case ModulePermission.TRADING_ACCOUNTS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return canViewTradingAccounts();
                    case PermissionAction.EDIT:
                        return canEditTradingAccounts();
                    default:
                        return false;
                }
            case ModulePermission.TRANSACTIONS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return canViewTransactions();
                    case PermissionAction.EDIT:
                        return canEditTransactions();
                    default:
                        return false;
                }
            default:
                return false;
        }
    };

    const hasAccess = checkPermission();
    return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Trading permission group component
 * Allows checking multiple permissions at once
 */
export const TradingPermissionGroup: React.FC<TradingPermissionGroupProps> = ({
    permissions,
    children,
    fallback = null,
    requireAll = false,
}) => {
    const {
        canViewOrders,
        canCreateOrders,
        canEditOrders,
        canDeleteOrders,
        canExportOrders,
        canViewTrades,
        canCreateTrades,
        canEditTrades,
        canDeleteTrades,
        canExportTrades,
        canViewPositions,
        canCreatePositions,
        canEditPositions,
        canViewCustomers,
        canEditCustomers,
        canViewTradingAccounts,
        canEditTradingAccounts,
        canViewTransactions,
        canEditTransactions,
    } = useTradingPermissions();

    const checkPermissions = () => {
        if (requireAll) {
            // All permissions must be met
            return permissions.every(({ module, action }) => {
                switch (module) {
                    case ModulePermission.ORDERS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewOrders();
                            case PermissionAction.CREATE:
                                return canCreateOrders();
                            case PermissionAction.EDIT:
                                return canEditOrders();
                            case PermissionAction.DELETE:
                                return canDeleteOrders();
                            case PermissionAction.EXPORT:
                                return canExportOrders();
                            default:
                                return false;
                        }
                    case ModulePermission.TRADES:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewTrades();
                            case PermissionAction.CREATE:
                                return canCreateTrades();
                            case PermissionAction.EDIT:
                                return canEditTrades();
                            case PermissionAction.DELETE:
                                return canDeleteTrades();
                            case PermissionAction.EXPORT:
                                return canExportTrades();
                            default:
                                return false;
                        }
                    case ModulePermission.TRADING:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewPositions();
                            case PermissionAction.CREATE:
                                return canCreatePositions();
                            case PermissionAction.EDIT:
                                return canEditPositions();
                            default:
                                return false;
                        }
                    case ModulePermission.CUSTOMERS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewCustomers();
                            case PermissionAction.EDIT:
                                return canEditCustomers();
                            default:
                                return false;
                        }
                    case ModulePermission.TRADING_ACCOUNTS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewTradingAccounts();
                            case PermissionAction.EDIT:
                                return canEditTradingAccounts();
                            default:
                                return false;
                        }
                    case ModulePermission.TRANSACTIONS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewTransactions();
                            case PermissionAction.EDIT:
                                return canEditTransactions();
                            default:
                                return false;
                        }
                    default:
                        return false;
                }
            });
        } else {
            // Any permission must be met
            return permissions.some(({ module, action }) => {
                switch (module) {
                    case ModulePermission.ORDERS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewOrders();
                            case PermissionAction.CREATE:
                                return canCreateOrders();
                            case PermissionAction.EDIT:
                                return canEditOrders();
                            case PermissionAction.DELETE:
                                return canDeleteOrders();
                            case PermissionAction.EXPORT:
                                return canExportOrders();
                            default:
                                return false;
                        }
                    case ModulePermission.TRADES:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewTrades();
                            case PermissionAction.CREATE:
                                return canCreateTrades();
                            case PermissionAction.EDIT:
                                return canEditTrades();
                            case PermissionAction.DELETE:
                                return canDeleteTrades();
                            case PermissionAction.EXPORT:
                                return canExportTrades();
                            default:
                                return false;
                        }
                    case ModulePermission.TRADING:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewPositions();
                            case PermissionAction.CREATE:
                                return canCreatePositions();
                            case PermissionAction.EDIT:
                                return canEditPositions();
                            default:
                                return false;
                        }
                    case ModulePermission.CUSTOMERS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewCustomers();
                            case PermissionAction.EDIT:
                                return canEditCustomers();
                            default:
                                return false;
                        }
                    case ModulePermission.TRADING_ACCOUNTS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewTradingAccounts();
                            case PermissionAction.EDIT:
                                return canEditTradingAccounts();
                            default:
                                return false;
                        }
                    case ModulePermission.TRANSACTIONS:
                        switch (action) {
                            case PermissionAction.VIEW:
                                return canViewTransactions();
                            case PermissionAction.EDIT:
                                return canEditTransactions();
                            default:
                                return false;
                        }
                    default:
                        return false;
                }
            });
        }
    };

    const hasAccess = checkPermissions();
    return hasAccess ? <>{children}</> : <>{fallback}</>;
};

// Convenience components for common trading actions
export const OrderViewGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.VIEW}
        {...props}
    />
);

export const OrderEditGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.EDIT}
        {...props}
    />
);

export const OrderCreateGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.CREATE}
        {...props}
    />
);

export const OrderDeleteGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.DELETE}
        {...props}
    />
);

export const OrderExportGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.EXPORT}
        {...props}
    />
);

export const TradeViewGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.VIEW}
        {...props}
    />
);

export const TradeEditGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.EDIT}
        {...props}
    />
);

export const TradeDeleteGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.DELETE}
        {...props}
    />
);

export const TradeExportGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.EXPORT}
        {...props}
    />
);

export const PositionViewGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.TRADING}
        action={PermissionAction.VIEW}
        {...props}
    />
);

export const PositionEditGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.TRADING}
        action={PermissionAction.EDIT}
        {...props}
    />
);

export const CustomerViewGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.CUSTOMERS}
        action={PermissionAction.VIEW}
        {...props}
    />
);

export const CustomerEditGate: React.FC<
    Omit<TradingPermissionGateProps, 'module' | 'action'>
> = props => (
    <TradingPermissionGate
        module={ModulePermission.CUSTOMERS}
        action={PermissionAction.EDIT}
        {...props}
    />
);

// Bulk operations gate
export const BulkOperationsGate: React.FC<
    Omit<TradingPermissionGroupProps, 'permissions'>
> = props => (
    <TradingPermissionGroup
        permissions={[
            { module: ModulePermission.ORDERS, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADES, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADING, action: PermissionAction.EDIT },
        ]}
        {...props}
    />
);

// Export gate for trading data
export const TradingExportGate: React.FC<
    Omit<TradingPermissionGroupProps, 'permissions'>
> = props => (
    <TradingPermissionGroup
        permissions={[
            { module: ModulePermission.ORDERS, action: PermissionAction.EXPORT },
            { module: ModulePermission.TRADES, action: PermissionAction.EXPORT },
            { module: ModulePermission.TRADING, action: PermissionAction.EXPORT },
        ]}
        {...props}
    />
);

// Analytics gate for trading data
export const TradingAnalyticsGate: React.FC<
    Omit<TradingPermissionGroupProps, 'permissions'>
> = props => (
    <TradingPermissionGroup
        permissions={[
            { module: ModulePermission.ORDERS, action: PermissionAction.VIEW },
            { module: ModulePermission.TRADES, action: PermissionAction.VIEW },
            { module: ModulePermission.TRADING, action: PermissionAction.VIEW },
        ]}
        {...props}
    />
);
